import { useState, useMemo, useCallback } from 'react'
import { LayoutGrid, List as ListIcon } from 'lucide-react'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { IconButton, Typogaphy } from 'vnetwork-ui'
import { getSubcontractProcesses } from '../../services'
import { ContractProcess } from '../../../../../Process/types'
import ContractProcessButtons from '../../shared/Buttons'
import ProcessTimeline from '../../../../../Process/components/List/ProcessTimeline'
import ProcessDetailDrawer from '../../../../../Process/components/List/ProcessDetailDrawer'
import { useProcessColumns } from '../../../../../Process/components/List/use-process-columns'
import { useProcessActions } from '../../../../../Process/components/List/use-process-actions'

type ViewMode = 'table' | 'timeline'

const List = () => {
    const { contractID, subcontractID } = useParams()
    const { t } = useTranslation()
    const [viewMode, setViewMode] = useState<ViewMode>('table')
    const [searchParams, setSearchParams] = useSearchParams()

    const { columns, attachmentExpandable } = useProcessColumns()
    const { actions } = useProcessActions()

    const { data: processes, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SUBCONTRACTS_PROCESSES,
            contractID,
            subcontractID,
        ],
        queryFn: () =>
            getSubcontractProcesses({
                contractID: Number(contractID),
                subcontractID: Number(subcontractID),
            }),
    })

    // URL-driven process selection for deep-linking & browser history
    const processIdFromUrl = searchParams.get('process_id')
    const selectedProcessId = processIdFromUrl ? Number(processIdFromUrl) : null

    const selectedProcess = useMemo(() => {
        if (!selectedProcessId || !processes) return null
        return processes.find(
            (p: ContractProcess) => p.contract_process_id === selectedProcessId
        )
    }, [selectedProcessId, processes])

    const handleSelectProcess = useCallback(
        (row: ContractProcess) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set('process_id', String(row.contract_process_id))
                    return newParams
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )

    const handleDrawerClose = useCallback(() => {
        setSearchParams(
            (prev) => {
                const newParams = new URLSearchParams(prev)
                newParams.delete('process_id')
                return newParams
            },
            { replace: true }
        )
    }, [setSearchParams])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Paper className="mt-2">
            <div className="flex items-center justify-between mb-3">
                <Typogaphy className="font-medium" size="small">
                    {t('contract-process-list')}
                </Typogaphy>
                <div className="flex items-center gap-1">
                    <IconButton
                        size="small"
                        onClick={() => setViewMode('table')}
                        className={
                            viewMode === 'table'
                                ? 'text-primary'
                                : 'text-gray-400'
                        }
                    >
                        <ListIcon className="w-4 h-4" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setViewMode('timeline')}
                        className={
                            viewMode === 'timeline'
                                ? 'text-primary'
                                : 'text-gray-400'
                        }
                    >
                        <LayoutGrid className="w-4 h-4" />
                    </IconButton>
                    <ContractProcessButtons.Create
                        contractID={Number(contractID)}
                        subcontractID={Number(subcontractID)}
                    />
                </div>
            </div>

            {viewMode === 'table' ? (
                <DataTableCustomized<ContractProcess>
                    columns={columns}
                    rows={processes || []}
                    rowKey={(row) => String(row.contract_process_id)}
                    size="small"
                    resizable
                    actions={actions}
                    expandable={attachmentExpandable}
                    onRowClick={handleSelectProcess}
                    emptyMessage={t('contract-process-empty')}
                    toolbar={{
                        settings: {
                            show: false,
                            storageKey: 'subcontract-processes',
                        },
                    }}
                />
            ) : (
                <>
                    {processes && processes.length > 0 ? (
                        <ProcessTimeline
                            processes={processes}
                            selectedProcessId={selectedProcessId}
                            onSelect={handleSelectProcess}
                        />
                    ) : (
                        <div className="h-20 flex justify-center items-center">
                            <Typogaphy className="text-gray-500">
                                {t('contract-process-empty')}
                            </Typogaphy>
                        </div>
                    )}
                </>
            )}

            <ProcessDetailDrawer
                process={selectedProcess}
                contractID={Number(contractID)}
                shouldCheckRegistrationDate={false}
                onClose={handleDrawerClose}
            />
        </Paper>
    )
}

export default List
